// Anonymous page-view tracking for the guest site. Fired once per route
// change from Layout.tsx, which wraps every guest page (including any
// admin-created custom page) behind a single shared <Outlet />, so this
// requires no per-page wiring — new pages are tracked automatically.

const API_BASE: string = import.meta.env.VITE_API_BASE ?? '/api';
const VISITOR_ID_KEY = 'hs_visitor_id';

function getVisitorId(): string {
  try {
    let id = localStorage.getItem(VISITOR_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(VISITOR_ID_KEY, id);
    }
    return id;
  } catch {
    // localStorage unavailable (private browsing lockdown, etc.) — fall back
    // to a per-load id rather than breaking tracking entirely.
    return crypto.randomUUID();
  }
}

export function trackPageView(path: string, pageTitle: string): void {
  const payload = JSON.stringify({
    path,
    page_title: pageTitle,
    visitor_id: getVisitorId(),
    referrer: document.referrer || '',
  });

  try {
    // "record", not "track" — ad blockers and privacy extensions (uBlock,
    // AdGuard, Safari content blockers) commonly pattern-match the literal
    // word "track" in any request URL, first-party or not, and silently
    // drop it — which would mean the site owner's own browsing never gets
    // counted even though the backend is otherwise working correctly.
    const url = `${API_BASE}/page_views.php?action=record`;
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      const sent = navigator.sendBeacon(url, blob);
      if (sent) return;
    }
    // Fallback for browsers without sendBeacon, or if it declined to queue.
    fetch(url, { method: 'POST', body: payload, keepalive: true }).catch(() => {});
  } catch {
    // Never let tracking break the page.
  }
}

// Most pages get their <title> synchronously (react-helmet-async flushes it
// during React's commit), but pages that fetch their content before
// rendering their <Seo>/<Helmet> (custom pages, blog posts) only get a real
// title once that fetch resolves. On the way there, react-helmet-async
// first clears the previous page's <title> to empty as it unmounts, then
// (measured in practice) can take well over a second before the new page's
// real title lands — so this debounces on every title mutation, but never
// treats an *empty* title as "settled": that's always a transient in-between
// state, not a real title, so only the maxWaitMs ceiling can force a send
// while it's empty. It always sends exactly once: if the visitor navigates
// away before the title settles, the caller's cleanup fires this immediately
// with whatever title is available rather than losing the pageview outright
// — an occasionally-stale/blank title is an acceptable cosmetic tradeoff, a
// silently dropped pageview is not.
export function schedulePageViewTracking(path: string, maxWaitMs = 2500, quietMs = 200): () => void {
  let settled = false;
  let quietTimer: number | undefined;

  const send = () => {
    if (settled) return;
    settled = true;
    observer?.disconnect();
    window.clearTimeout(maxTimer);
    window.clearTimeout(quietTimer);
    trackPageView(path, document.title);
  };

  const restartQuietTimer = () => {
    window.clearTimeout(quietTimer);
    if (!document.title) return; // empty is never a real, settled title
    quietTimer = window.setTimeout(send, quietMs);
  };

  // `document.title = '...'` updates the existing text node's character
  // data in place rather than swapping child nodes, so childList alone
  // never fires — characterData + subtree is what actually catches it.
  const titleEl = document.querySelector('title');
  const observer = titleEl ? new MutationObserver(restartQuietTimer) : null;
  observer?.observe(titleEl!, { childList: true, characterData: true, subtree: true });

  // Start the quiet clock immediately too, for the common case where the
  // title never changes again after this point.
  restartQuietTimer();
  const maxTimer = window.setTimeout(send, maxWaitMs);

  // Called on unmount/pathname change. If the title hadn't settled yet,
  // send now (guaranteed-once) instead of leaving the observer to fire
  // later against a title that by then belongs to a different page.
  return () => {
    if (!settled) send();
  };
}
